import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';
import { normalizeSupplier } from '@/features/inventory/suppliers/suppliersSlice';

const BASE_URL = process.env.NEXT_PUBLIC_INVENTORY_API_URL ?? 'http://localhost:8082';

export interface ApiResponseBase<T = unknown> {
  success: boolean;
  message: string;
  data: T;
}

export type SupplierItem = {
  id: number;
  name: string;
  contact_info?: string | null;
  is_placeholder?: boolean;
};

export const suppliersApi = createApi({
  reducerPath: 'suppliersApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['Suppliers'],
  endpoints: (builder) => ({
    // GET /v1/suppliers?query=&limit=
    getSuppliers: builder.query<
      ApiResponseBase<{ items: SupplierItem[] }>,
      { query?: string; limit?: number } | void
    >({
      query: (args) => {
        const q = args?.query ? `?query=${encodeURIComponent(args.query)}${args?.limit ? `&limit=${args.limit}` : ''}` : args?.limit ? `?limit=${args.limit}` : '';
        return { url: `/v1/suppliers${q}`, method: 'GET' } as const;
      },
      transformResponse: (resp: any): ApiResponseBase<{ items: SupplierItem[] }> => {
        const rawItems: any[] = resp?.data?.items ?? [];
        const items: SupplierItem[] = rawItems.map(normalizeSupplier);
        return { success: !!resp?.success, message: String(resp?.message ?? ''), data: { items } };
      },
      transformErrorResponse: handlerErrorResponse('Suppliers.List'),
      providesTags: ['Suppliers'],
    }),

    // GET /v1/suppliers/{id}
    getSupplier: builder.query<ApiResponseBase<SupplierItem>, number | string>({
      query: (id) => ({ url: `/v1/suppliers/${id}`, method: 'GET' }),
      transformResponse: (resp: any): ApiResponseBase<SupplierItem> => {
        const it = resp?.data ?? {};
        const normalized = normalizeSupplier(it);
        return { success: !!resp?.success, message: String(resp?.message ?? ''), data: normalized };
      },
      transformErrorResponse: handlerErrorResponse('Suppliers.Get'),
      providesTags: (_r, _e, id) => [{ type: 'Suppliers', id: String(id) }],
    }),

    // POST /v1/suppliers
    createSupplier: builder.mutation<ApiResponseBase<SupplierItem>, { name: string; contact_info?: string | null }>({
      query: (body) => ({ url: '/v1/suppliers', method: 'POST', body }),
      transformResponse: (resp: any): ApiResponseBase<SupplierItem> => {
        const data = normalizeSupplier(resp?.data ?? resp);
        return { success: !!resp?.success, message: String(resp?.message ?? ''), data };
      },
      transformErrorResponse: handlerErrorResponse('Suppliers.Create'),
      invalidatesTags: ['Suppliers'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al crear proveedor',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Suppliers.Create'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'suppliers.create',
              endpointName: e?.endpointName || 'Suppliers.Create',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // PUT /v1/suppliers/{id}
    updateSupplier: builder.mutation<ApiResponseBase<any>, { id: number | string; body: { name?: string; contact_info?: string | null } }>({
      query: ({ id, body }) => ({ url: `/v1/suppliers/${id}`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Suppliers.Update'),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Suppliers', id: String(arg.id) }, 'Suppliers'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar proveedor',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Suppliers.Update'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'suppliers.update',
              endpointName: e?.endpointName || 'Suppliers.Update',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
  }),
});

export const {
  useGetSuppliersQuery,
  useLazyGetSuppliersQuery,
  useGetSupplierQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
} = suppliersApi;
